import { useState } from 'react'
import { Check, Copy, Navigation } from 'lucide-react'
import { koordDezimal, koordGradMinuten, koordMgrs, flaecheM2, textFlaecheStufen, type Pkt } from './geodaesie'

/*
 * Infobox für Markierungen (z. B. aus DJI Pilot 2): Name, Zeit und Koordinaten in Dezimalgrad (WGS84), Grad/Minuten und
 * UTMREF/MGRS – je mit Kopierknopf – sowie Link „In Karten-App öffnen“ (Navigation zu Fuß).
 * Reiner Inhalt ohne Leaflet-Abhängigkeit: wird in <Popup> der Karten (Führung, Co-Pilot, externe Freigabeseite) eingesetzt.
 * Inline-Styles, damit die Box auch im hellen Leaflet-Popup unabhängig vom Seitenthema gut lesbar ist.
 */

export interface MarkierungGeometrie { type?: string; coordinates?: any }
export interface MarkierungPunkt { label: string; pkt: Pkt }

/** GeoJSON ([lon, lat]) → bezeichnete Punkte [lat, lon]. Punkt: 1 Punkt; Linie: Mitte, Start, Ende; Fläche: Mittelpunkt + Eckpunkte. */
export function markierungPunkte(g: MarkierungGeometrie | undefined | null): MarkierungPunkt[] {
  if (!g || !Array.isArray(g.coordinates)) return []
  const ll = (p: number[]): Pkt => [p[1], p[0]]
  const gueltig = (p: any): p is number[] => Array.isArray(p) && isFinite(p[0]) && isFinite(p[1])
  const mittel = (pk: Pkt[]): Pkt => [pk.reduce((s, p) => s + p[0], 0) / pk.length, pk.reduce((s, p) => s + p[1], 0) / pk.length]
  if (g.type === 'Point') return gueltig(g.coordinates) ? [{ label: 'Position', pkt: ll(g.coordinates) }] : []
  if (g.type === 'LineString') {
    const pk = (g.coordinates as any[]).filter(gueltig).map(ll)
    if (!pk.length) return []
    return [{ label: 'Mitte der Linie', pkt: mittel(pk) }, { label: 'Start', pkt: pk[0] }, ...(pk.length > 1 ? [{ label: 'Ende', pkt: pk[pk.length - 1] }] : [])]
  }
  if (g.type === 'Polygon' && Array.isArray(g.coordinates[0])) {
    let pk = (g.coordinates[0] as any[]).filter(gueltig).map(ll)
    if (pk.length > 1 && pk[0][0] === pk[pk.length - 1][0] && pk[0][1] === pk[pk.length - 1][1]) pk = pk.slice(0, -1)   // Ring schließt mit Startpunkt
    if (!pk.length) return []
    return [{ label: 'Mittelpunkt', pkt: mittel(pk) }, ...pk.map((p, i) => ({ label: `Eckpunkt ${i + 1}`, pkt: p }))]
  }
  return []
}

/** Fläche (m²) einer Polygon-Markierung (GeoJSON), sonst 0 */
export function flaechenDerGeometrie(g: MarkierungGeometrie | undefined | null): number {
  if (!g || g.type !== 'Polygon' || !Array.isArray(g.coordinates?.[0])) return 0
  const pk: Pkt[] = (g.coordinates[0] as any[]).filter(p => Array.isArray(p) && isFinite(p[0]) && isFinite(p[1])).map(p => [p[1], p[0]] as Pkt)
  return flaecheM2(pk)
}

/** Link zur Karten-App mit Fußnavigation: Android geo:, Apple Maps auf iOS/macOS, sonst Google Maps */
export function kartenAppLink(p: Pkt, name?: string | null): string {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  const [lat, lon] = [p[0].toFixed(6), p[1].toFixed(6)]
  if (/Android/i.test(ua)) return `geo:${lat},${lon}?q=${lat},${lon}(${encodeURIComponent(name || 'Markierung')})`
  if (/iPhone|iPad|iPod|Macintosh/i.test(ua)) return `https://maps.apple.com/?daddr=${lat},${lon}&dirflg=w`
  return googleLink(p)
}
export const googleLink = (p: Pkt) => `https://www.google.com/maps/dir/?api=1&destination=${p[0].toFixed(6)},${p[1].toFixed(6)}&travelmode=walking`

async function kopieren(text: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(text); return true } catch { /* z. B. http ohne Clipboard-API */ }
  try {
    const ta = document.createElement('textarea')
    ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0'
    document.body.appendChild(ta); ta.select()
    const ok = document.execCommand('copy')
    ta.remove()
    return ok
  } catch { return false }
}

function Zeile({ titel, wert }: { titel: string; wert: string }) {
  const [ok, setOk] = useState(false)
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.04em' }}>{titel}</div>
        <div data-testid={`koord-${titel}`} style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13, color: '#111827', wordBreak: 'break-word' }}>{wert}</div>
      </div>
      <button type="button" aria-label={`${titel} kopieren`} title="Kopieren"
        onClick={async e => { e.stopPropagation(); if (await kopieren(wert)) { setOk(true); setTimeout(() => setOk(false), 1500) } }}
        style={{ flexShrink: 0, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #d1d5db', borderRadius: 6, background: ok ? '#dcfce7' : '#fff', color: '#374151', cursor: 'pointer' }}>
        {ok ? <Check size={14} /> : <Copy size={14} />}
      </button>
    </div>
  )
}

/** Alle drei Koordinatenformate eines Punktes untereinander (mit Kopierknöpfen) */
export function KoordinatenBlock({ pkt }: { pkt: Pkt }) {
  return (
    <>
      <Zeile titel="Dezimalgrad (WGS84)" wert={koordDezimal(pkt)} />
      <Zeile titel="Grad / Minuten" wert={koordGradMinuten(pkt)} />
      <Zeile titel="UTMREF / MGRS" wert={koordMgrs(pkt)} />
    </>
  )
}

export function KartenAppLink({ pkt, name }: { pkt: Pkt; name?: string | null }) {
  return (
    <a href={kartenAppLink(pkt, name)} target="_blank" rel="noopener noreferrer" data-testid="karten-app-link"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, padding: '6px 10px', borderRadius: 6, background: '#1d4ed8', color: '#fff', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
      <Navigation size={14} /> In Karten-App öffnen
    </a>
  )
}

/** Inhalt des Popups einer Markierung */
export function MarkierungInfo({ name, zeit, quelle, geometrie }: { name?: string | null; zeit?: string | null; quelle?: string; geometrie: MarkierungGeometrie | undefined | null }) {
  const punkte = markierungPunkte(geometrie)
  const uhr = zeit ? new Date(zeit).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : null
  const [haupt, ...weitere] = punkte
  const flaeche = flaechenDerGeometrie(geometrie)
  return (
    <div style={{ minWidth: 230, maxWidth: 290 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{name || 'Markierung'}</div>
      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>{[quelle, uhr && `${uhr} Uhr`].filter(Boolean).join(' · ')}</div>
      {flaeche > 0 && <div data-testid="markierung-flaeche" style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 4 }}>Fläche: {textFlaecheStufen(flaeche)}</div>}
      {haupt ? (
        <>
          {punkte.length > 1 && <div style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>{haupt.label}</div>}
          <KoordinatenBlock pkt={haupt.pkt} />
          <KartenAppLink pkt={haupt.pkt} name={name} />
          {weitere.length > 0 && (
            <details style={{ marginTop: 6 }}>
              <summary style={{ fontSize: 12, color: '#374151', cursor: 'pointer' }}>Weitere Punkte ({weitere.length})</summary>
              {weitere.slice(0, 12).map(w => (
                <div key={w.label} style={{ borderTop: '1px solid #e5e7eb', marginTop: 4 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#374151', paddingTop: 3 }}>{w.label}</div>
                  <Zeile titel="UTMREF / MGRS" wert={koordMgrs(w.pkt)} />
                  <Zeile titel="Dezimalgrad (WGS84)" wert={koordDezimal(w.pkt)} />
                </div>
              ))}
            </details>
          )}
        </>
      ) : <div style={{ fontSize: 12, color: '#6b7280' }}>Keine Koordinaten</div>}
    </div>
  )
}
